import { useEffect, useState } from "react"

import { useGetUserSettingsQuery } from "@/entities/user/settings"

import { isFetchBaseQueryError } from "@/shared/lib/utils/redux-errors"
import { buildMainBotSettingsLink, parseStartParams } from "@/shared/router"
import { WebApp } from "@/shared/telegram"

import { useAppContext } from "../app-provider"

// A slow API must not keep a main launch behind the loader; a 401 that arrives later still switches to the redirect.
const AUTH_CHECK_TIMEOUT_MS = 2000

export type CloneLaunchState =
  | { kind: "pass" }
  | { kind: "checking" }
  | { kind: "redirect"; url: string }
  | { kind: "auth-error" }

/**
 * The boot read of user settings is the only request this reads, and LanguageProvider already issues it. With
 * non-empty initData the API answers 401 only when the HMAC does not match the main bot token, which is what a
 * launch from a clone's own Mini App looks like.
 */
const useLaunchAuthCheck = () => {
  const { captchaMode } = useAppContext()
  const exempt = WebApp.initData === "" || captchaMode
  const { rejected, settled } = useGetUserSettingsQuery(undefined, {
    skip: exempt,
    selectFromResult: ({ error, isError, isSuccess }) => ({
      rejected: isError && isFetchBaseQueryError(error) && error.status === 401,
      settled: isSuccess || isError
    })
  })
  return { exempt, rejected, settled }
}

export const useLaunchAuthRejected = (): boolean => useLaunchAuthCheck().rejected

/**
 * A launch that can be a clone's shows only the neutral loader until the boot read settles, so the generic app
 * shell never flashes before the jump. Both outcomes are latched: a later refetch of the same entry (which clears
 * isError and isSuccess while pending) can neither bring the app back after a 401 nor put the loader over a
 * running app.
 */
export const useCloneLaunchState = (): CloneLaunchState => {
  const { chatId, isInitialized, tabParam } = useAppContext()
  const [carriesLoopMarker] = useState(() => parseStartParams().cloneRedirect)
  const { exempt, rejected, settled } = useLaunchAuthCheck()
  const [wasRejected, setWasRejected] = useState(false)
  const [released, setReleased] = useState(false)

  if (rejected && !wasRejected) setWasRejected(true)
  if (settled && !rejected && !released) setReleased(true)

  const waiting = !exempt && !released && !wasRejected && !rejected
  useEffect(() => {
    if (!waiting) return
    const timeoutId = window.setTimeout(() => {
      setReleased(true)
    }, AUTH_CHECK_TIMEOUT_MS)
    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [waiting])

  if (exempt) return { kind: "pass" }

  if (rejected || wasRejected) {
    // Only the redirect link carries the marker, so this launch came from the main bot and its sign-in really failed.
    if (carriesLoopMarker) return { kind: "auth-error" }
    if (!isInitialized) return { kind: "checking" }
    const url = buildMainBotSettingsLink({ chatId, tab: tabParam })
    return url ? { kind: "redirect", url } : { kind: "auth-error" }
  }

  return released ? { kind: "pass" } : { kind: "checking" }
}
